import React, { useState } from "react";
import { PromoBanner } from "../../components/PromoBanner";
import { usePromoHolidays, promoFromHoliday, createPromoCode } from "./promoHolidays";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const PromoEditor = ({ promo, onChange }) => {
  const set = (k, v) => onChange({ ...promo, [k]: v });
  const [autoCode, setAutoCode] = useState(true);
  const [hol] = usePromoHolidays();
  const presets = (hol?.holidays || []).filter((h) => h.enabled);
  const applyPreset = async (p) => {
    onChange({ ...promo, ...promoFromHoliday(p) });
    if (autoCode) await createPromoCode(p, " Remember to save your storefront.");
  };
  const input = "w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]";
  return (
    <div className="border-t border-gray-100 pt-4" data-testid="promo-editor">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-semibold text-[#16243b] text-sm">Promo banner</h4>
        <label className="flex items-center gap-2 text-sm text-[#16243b]">
          <input type="checkbox" checked={!!promo.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="promo-enabled" /> Show on storefront
        </label>
      </div>
      <p className="text-xs text-[#8a97a8] mb-3">Run a seasonal offer, e.g. “Mother’s Day – 10% off all vouchers”. Add a discount code (create it in the Discounts tab) and it’s applied automatically at checkout.</p>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="promo-presets">
        {presets.map((p) => (
          <button type="button" key={p.id} data-testid={`promo-preset-${p.id}`}
            onClick={() => applyPreset(p)}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: p.bg_color, color: p.text_color }}>
            {p.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-[#16243b] -mt-1 mb-3">
        <input type="checkbox" checked={autoCode} onChange={(e) => setAutoCode(e.target.checked)} data-testid="promo-auto-code" />
        Also create matching discount code (e.g. MUM10 = 10% off, expiring with the promo)
      </label>
      <div className="space-y-3">
        <input value={promo.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="Mother’s Day – 10% off all vouchers" data-testid="promo-text" className={input} />
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={promo.discount_code || ""} onChange={(e) => set("discount_code", e.target.value.toUpperCase())} placeholder="Discount code (optional)" data-testid="promo-code" className={`${input} font-mono uppercase`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-[#5a6b80]">Starts (blank = straight away)
            <input type="datetime-local" value={toLocalInput(promo.starts_at)} onChange={(e) => set("starts_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="promo-starts-at" className={input} />
          </label>
          <label className="text-xs text-[#5a6b80]">Ends (blank = no end)
            <input type="datetime-local" value={toLocalInput(promo.ends_at)} onChange={(e) => set("ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="promo-ends-at" className={input} />
          </label>
        </div>
        {promo.starts_at && promo.ends_at && new Date(promo.ends_at) <= new Date(promo.starts_at) && (
          <p className="text-xs text-red-500" data-testid="promo-date-error">The end must be after the start.</p>
        )}
        {promo.enabled && promo.starts_at && new Date(promo.starts_at) > new Date() && (
          <p className="text-xs text-[#2a4a6b] bg-[#eef7ff] rounded-lg px-3 py-2" data-testid="promo-scheduled-note">Scheduled — goes live {new Date(promo.starts_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}.</p>
        )}
        <div className="flex flex-wrap items-center gap-4 text-sm text-[#16243b]">
          {[["Banner colour", "bg_color", "#f43f5e"], ["Banner text", "text_color", "#ffffff"]].map(([label, key, def]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="color" value={promo[key] || def} onChange={(e) => set(key, e.target.value)} data-testid={`promo-${key}`} className="w-10 h-9 rounded cursor-pointer border border-gray-200" /> {label}
            </label>
          ))}
          <span className="text-xs text-[#8a97a8]">Countdown shows when an end date is set.</span>
        </div>
        {promo.enabled && promo.text && <div className="rounded-xl overflow-hidden" data-testid="promo-preview"><PromoBanner promo={promo} /></div>}
      </div>
    </div>
  );
};
